import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { isStrEmpty } from '@eforms/forms-common-ui';
import { DEFINITION_ROOT, LWB_REDIRECT_API_PATH } from '../constants/ApiEndPoint';
import { useSetState } from '@zambezi/sdk/common';
import { getError, getSchemaError } from './getError';
import { getAccessToken } from './authUtils';
import { autopopulateCustomerDetails, getCustomerDetails } from './autopopulateCustomerDetails';
import request from './request';
import { updateDefaultBasicTag } from '../redux/analyticsSlice';
import { analyticsApplicationStart } from '../components/analyticsTrack';
import { FORM_FEEDBACK_SAVE } from '../constants/ApiEndPoint';
import { AUTHENTICATION_STATUS } from '../components/Authentication/IAMAuthentication';
import { runtimeRequester } from "@eforms/forms-common-ui";

// const isAnalyticsEnabled = window.environmentConfig && window.environmentConfig.isAnalytics

export function useForm (definitionUuid, query, authenticationStatus) {
    const dispatch = useDispatch();
    const [state, setState] = useSetState({
        initialFormid: null,
        formDefinition: null,
        isLoading: true,
        error: null,
        customerDetails: null
    })
    useEffect(() => {
        async function fetchFormDetails() {
            if (!definitionUuid) return

            // Form already loaded and user is navigating to a different form by changing the form id in the url.
            // This handles form switching scenarios where:
            // 1. User initially loaded form A (state.initialFormid is set)
            // 2. User navigates to form B (definitionUuid changes)
            // 3. We need to clear sessionStorage to ensure that form B doesn't inherit any cached data like access token from form A
            // 4. Full page reload ensures clean initialization of the new form
            if (state.initialFormid !== null && state.initialFormid !== definitionUuid) {
                sessionStorage.clear();
                window.location.reload();
                return;
            }

            let aborted = false;
            if (authenticationStatus === AUTHENTICATION_STATUS.FAILURE) {
                setState((prev)=>({ ...prev, isLoading: false }))
                return
            }
            const uri = [DEFINITION_ROOT, encodeURIComponent(definitionUuid)].join('/')
            const url = uri && uri + '?allow-templates=false'
            const definitionRequest = request
                .get(url)
                .accept('json')
                .set('x-form-uuid', definitionUuid)

            if(authenticationStatus === AUTHENTICATION_STATUS.SUCCESS) {
                definitionRequest.set('Authorization', `Bearer ${getAccessToken()}`);
                definitionRequest.withCredentials();
            }

            const formRequest = { body: { data: {} } }
            const apiRequests = [definitionRequest, formRequest];
            setState({ isLoading: true })
            Promise.all(apiRequests)
                .then(results => results.map(r => r.body || r.text))
                .then(async([formDefinition, formData, selectedBusinessArea]) => {
                    if(!aborted) {
                        let customerDetails = null;
                        try {
                            if(formDefinition.isAuthEnabled) {
                                customerDetails = await getCustomerDetails(definitionUuid);
                                autopopulateCustomerDetails(formDefinition, customerDetails);
                            }
                        } catch(error) {
                            setState({ isLoading: false, error: getSchemaError(error), initialFormid: definitionUuid });
                            return;
                        }
                        setState({
                          formDefinition: sortStepDefinitions(formDefinition),
                          formData,
                          isLoading: false,
                          error: null,
                          customerDetails,
                          initialFormid: definitionUuid,
                        });

                        setTimeout(() => {
                            const defaultBasicTag = analyticsApplicationStart(formDefinition)
                            dispatch(updateDefaultBasicTag(defaultBasicTag))
                        }, 500);
                    }
                })
                .catch(
                    error => {
                        const errorStatus = getSchemaError(error);
                        if(authenticationStatus !== AUTHENTICATION_STATUS.SUCCESS && errorStatus === "ACCESS_DENIED") {
                            //If the form is authenticated then user need to login with bank, here redirecting the user to the banking site
                            redirectToLWB(definitionUuid).catch(redirectError => {
                                if (!aborted) {
                                    setState({ 
                                        isLoading: false,
                                        error: getSchemaError(redirectError),
                                        initialFormid: definitionUuid
                                    });
                                }
                            });
                        return;
                    }
                
                    if (!aborted) {
                        setState({ isLoading: false, error: errorStatus, initialFormid: definitionUuid });
                    }
                })

            return () => {
                aborted = true
                definitionRequest.abort()
                formRequest.abort()
            }
        }

        if (definitionUuid) {
            fetchFormDetails();
        }
    }, [definitionUuid, setState]);

    return state
}

export function useSubmitForm (data, isSavedData, authenticationStatus) {
    const [state, setState] = useSetState({
        isSaving: false,
        error: null,
        response: undefined
    })

    useEffect(() => {
        if (!data || isSavedData) return
        let aborted = false
        setState({ isSaving: true, error: null, response: undefined })
        const jsonRequest = request.post(`/forms`)

        if(authenticationStatus === AUTHENTICATION_STATUS.SUCCESS) {
            jsonRequest.set('Authorization', `Bearer ${getAccessToken()}`);
            jsonRequest.withCredentials();
        }

        jsonRequest
            .set('x-form-uuid', data.formDefinitionReference.uuid)
            .send(data)
            .then((res) => !aborted && setState({ isSaving: false, response:{uuid:res.text} }))
            .catch(
                error =>
                    !aborted && setState({ isSaving: false, error: getError(error) })
            )

        return () => {
            aborted = true
            jsonRequest.abort()
        }
    }, [data, setState])
    return state
}
export function sortStepDefinitions(formDefinition) {
    return {...formDefinition, stepDefinitions: [...formDefinition.stepDefinitions.sort((a,b)=> a.sequenceNumber - b.sequenceNumber)]}
}
export function getThemesByBrand(brand) {
    if (!window.environmentConfig.brandToThemeMap) return;
    if (document.getElementById("favicon") && window.environmentConfig.brandToThemeMap[brand]) {
        const defaultTheme = 'zb-champion-standard-theme';
        document.body.classList.remove(defaultTheme);
        document.body.classList.add(window.environmentConfig.brandToThemeMap[brand].theme);
        document.getElementById("favicon").href = `favicon-${brand}.ico`
    } else {
        const defaultTheme = 'zb-champion-standard-theme';
        document.body.classList.remove(defaultTheme);
        document.body.classList.add(window.environmentConfig.brandToThemeMap.default.theme);
    }
    return;
}

/**
 * Fetches an API response and normalizes the result JSON according to data
 * This makes every API response have the same shape, regardless of how nested it was.
 * @param settings Will contain httpMethod, headers and body for the request
 * @param data it is payloadData
 * @returns Promise containing a json response 
 */
export const sendFeedbackApi = (settings = {}, data) => {
    return new Promise((resolve, reject) => {
        runtimeRequester.put(FORM_FEEDBACK_SAVE)
            .set('x-form-uuid', data.formDefinitionReference.uuid)
            .send(settings.body || null)
            .accept(settings.accept || '*/*')
            .then(
                (response) => {
                    return resolve(response)
                },
                (error) => {
                    return reject(error)
                },
            )
    })
}

/**
 * This function will redirect to LWB ( Login With Bank) .if the form is authenticated, then make the API call to get the LWB redirect URL 
 * and redirecting to the Login screen of the bank
 * @param definitionUuid form definition unique id
*/
const redirectToLWB = async (definitionUuid) => {
  try {
    const res = await request
      .get(`${LWB_REDIRECT_API_PATH}`)
      .set("x-form-uuid", definitionUuid);

    const { redirectUri } = res.body;

    if (isStrEmpty(redirectUri)) {
      throw new Error("INVALID_LWB_PARAMS");
    }
    
    const fullRedirectUrl = `${window.environmentConfig.lwb.lwbHostUri}${redirectUri}&redirect_uri=${window.environmentConfig.lwb.redirect_uri}/`;
    window.location.href = fullRedirectUrl;
  } catch (error) {
    if (
      error.status === 400 &&
      error.response?.body?.errorDescription === "LWB is not permitted"
    ) {
      throw new Error("LWB_NOT_ALLOWED");
    }
    throw new Error(`Failed to redirect to LWB: ${error.message}`);
  }
};